import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemedCard } from '../components/ThemedCard';
import { ThemedButton } from '../components/ThemedButton';
import { ArrowLeft, Users, Crown, LogOut, Play } from 'lucide-react';
import { useTheme } from '../lib/theme-provider';
import { supabase } from '../lib/supabase';
import RoomConfigPanel from '../components/RoomConfigPanel';
import PlayerSeatGrid from '../components/PlayerSeatGrid';
import SpectatorList from '../components/SpectatorList';
import PasswordInputModal from '../components/PasswordInputModal';
import ConfirmModal from '../components/ConfirmModal';

// 类型定义
interface RoomState {
  id: string;
  roomCode: string;
  name: string;
  status: 'waiting' | 'playing' | 'finished';
  maxPlayers: number;
  isLocked: boolean; // 配置是否锁定
  hasPassword: boolean; // 是否有密码保护
  hostId: string;
}

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  isHost: boolean;
  isReady: boolean;
  isOnline: boolean;
  avatar?: string;
}

interface Spectator {
  id: string;
  nickname: string;
  joinedAt: string;
}

interface RoomConfig {
  maxPlayers: number;
  goodRoles: {
    merlin: number;
    percival: number;
    loyal: number;
  };
  evilRoles: {
    mordred: number;
    morgana: number;
    assassin: number;
    oberon: number;
  };
  extensions: {
    ladyOfLake: boolean;
    excalibur: boolean;
  };
  forcedRound: 'none' | 'round3' | 'round5' | 'global3';
  publicVote: boolean;
}

// 默认配置
const defaultConfig: RoomConfig = {
  maxPlayers: 10,
  goodRoles: { merlin: 1, percival: 1, loyal: 5 },
  evilRoles: { mordred: 1, morgana: 1, assassin: 1, oberon: 1 },
  extensions: { ladyOfLake: false, excalibur: false },
  forcedRound: 'single3',
  publicVote: false,
};

// --- 主页面：房间准备 ---
const OnlineGameRoom = () => {
  const { theme } = useTheme();
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();

  // 状态
  const [room, setRoom] = useState<RoomState | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [spectators, setSpectators] = useState<Spectator[]>([]);
  const [config, setConfig] = useState<RoomConfig>(defaultConfig);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [isHost, setIsHost] = useState(false);
  const [isSpectator, setIsSpectator] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [joiningSeat, setJoiningSeat] = useState<number | null>(null);
  
  // 确认弹窗状态
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);

  // 从Supabase加载真实数据
  useEffect(() => {
    const loadRoomData = async () => {
      if (!roomId) return;

      try {
        // 1. 加载房间信息
        const { data: roomData, error: roomError } = await supabase
          .from('online_game_rooms_cce0d86f_8f5')
          .select('*')
          .eq('id', roomId)
          .single();

        if (roomError || !roomData) {
          console.error('加载房间失败:', roomError);
          navigate('/online-lobby');
          return;
        }

        setRoom({
          id: roomData.id,
          roomCode: roomData.room_code,
          name: roomData.room_name,
          status: roomData.status,
          maxPlayers: roomData.max_player_count,
          isLocked: roomData.is_locked || false,
          hasPassword: roomData.is_public === false,
          hostId: roomData.host_player_number.toString(),
        });

        // 2. 加载房间配置
        if (roomData.config) {
          setConfig({
            maxPlayers: roomData.max_player_count,
            goodRoles: roomData.config.good_roles || { merlin: 1, percival: 1, loyal: roomData.max_player_count - 6 },
            evilRoles: roomData.config.evil_roles || { mordred: 1, morgana: 1, assassin: 1, oberon: 1 },
            extensions: roomData.config.extensions || { ladyOfLake: false, excalibur: false },
            forcedRound: roomData.config.forced_round || 'single3',
            publicVote: roomData.config.public_vote || false,
          });
        }

        // 3. 加载玩家列表（带头像）
        const { data: playersData, error: playersError } = await supabase
          .from('online_room_players_cce0d86f_8f5')
          .select('*')
          .eq('room_id', roomId);

        if (playersError) {
          console.error('加载玩家失败:', playersError);
        } else if (playersData) {
          // 获取所有玩家的头像
          const playerIds = playersData.map(p => p.player_id);
          const { data: profilesData } = await supabase
            .from('game_user_profiles')
            .select('user_id, avatar_url')
            .in('user_id', playerIds);

          const avatarMap = new Map();
          profilesData?.forEach(p => avatarMap.set(p.user_id, p.avatar_url));

          const loadedPlayers = playersData.map(p => ({
            id: p.player_id,
            seatNumber: p.seat_number || 0,
            nickname: p.nickname,
            isHost: p.is_host,
            isReady: p.is_ready,
            isOnline: p.is_online,
            avatar: avatarMap.get(p.player_id) || undefined,
          }));

          setPlayers(loadedPlayers);
        }

        // 4. 加载观战者列表
        const { data: spectatorsData, error: spectatorsError } = await supabase
          .from('online_room_spectators_cce0d86f_8f5')
          .select('*')
          .eq('room_id', roomId);

        if (spectatorsError) {
          console.error('加载观战者失败:', spectatorsError);
        } else if (spectatorsData) {
          const loadedSpectators = spectatorsData.map(s => ({
            id: s.user_id,
            nickname: s.nickname,
            joinedAt: s.joined_at,
          }));
          setSpectators(loadedSpectators);
        }

        // 5. 设置当前用户状态（从localStorage获取）
        const currentUser = localStorage.getItem('currentUser');
        if (currentUser) {
          const user = JSON.parse(currentUser);
          setCurrentUserId(user.id);
          setIsHost(user.id === roomData.host_player_number.toString());
          
          // 检查是否是观战者
          const isSpec = spectatorsData?.some(s => s.user_id === user.id) || false;
          setIsSpectator(isSpec);
        }

      } catch (error) {
        console.error('加载房间数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRoomData();

    // 订阅房间数据变化
    const subscription = supabase
      .channel(`room:${roomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'online_game_rooms_cce0d86f_8f5', filter: `id=eq.${roomId}` }, (payload) => {
        // 房间数据更新时重新加载
        loadRoomData();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [roomId, navigate]);

  // 处理配置变更
  const handleConfigChange = (newConfig: RoomConfig) => {
    if (!isHost || room?.isLocked) return;
    setConfig(newConfig);
    // TODO: 同步到Supabase
  };

  // 锁定配置
  const handleLockConfig = () => {
    if (!isHost) return;
    setRoom((prev) => (prev ? { ...prev, isLocked: true } : null));
    // TODO: 同步到Supabase
  };

  // 解锁配置
  const handleUnlockConfig = () => {
    if (!isHost) return;
    setRoom((prev) => (prev ? { ...prev, isLocked: false } : null));
    // TODO: 同步到Supabase
  };

  // 点击就位
  const handleSitDown = (seatNumber: number) => {
    if (isSpectator) return;
    
    // 只有房间设置了密码时才需要输入密码，配置锁定不影响入座
    if (room?.hasPassword) {
      setJoiningSeat(seatNumber);
      setShowPasswordModal(true);
      return;
    }
    
    // 直接入座
    completeSitDown(seatNumber);
  };

  // 完成入座
  const completeSitDown = (seatNumber: number) => {
    // TODO: 同步到Supabase
    setPlayers((prev) =>
      prev.map((p) =>
        p.id === currentUserId ? { ...p, seatNumber } : p
      )
    );
  };

  // 密码验证成功
  const handlePasswordConfirm = (password: string) => {
    // TODO: 验证密码
    console.log('密码:', password);
    setShowPasswordModal(false);
    if (joiningSeat !== null) {
      completeSitDown(joiningSeat);
      setJoiningSeat(null);
    }
  };

  // 踢人
  const handleKickPlayer = (playerId: string) => {
    if (!isHost) return;
    // TODO: 同步到Supabase
    setPlayers((prev) => prev.filter((p) => p.id !== playerId));
  };

  // 转让房主
  const handleTransferHost = (playerId: string) => {
    if (!isHost) return;
    // TODO: 同步到Supabase
    setPlayers((prev) =>
      prev.map((p) => ({
        ...p,
        isHost: p.id === playerId,
      }))
    );
    setIsHost(false);
  };

  // 退出房间
  const handleLeaveRoom = async () => {
    setShowLeaveConfirm(true);
  };

  // 确认退出房间
  const confirmLeaveRoom = async () => {
    if (isHost && room) {
      // 房主退出，关闭房间
      await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .update({ status: 'closed', is_deleted: 'y' })
        .eq('id', room.id);
    }
    setShowLeaveConfirm(false);
    navigate('/online-lobby');
  };

  // 返回按钮点击
  const handleBackClick = () => {
    setShowBackConfirm(true);
  };

  // 确认返回
  const confirmBack = async () => {
    if (isHost && room) {
      // 房主退出，关闭房间
      await supabase
        .from('online_game_rooms_cce0d86f_8f5')
        .update({ status: 'closed', is_deleted: 'y' })
        .eq('id', room.id);
    }
    setShowBackConfirm(false);
    navigate('/online-lobby');
  };

  // 开始游戏
  const handleStartGame = () => {
    if (!isHost) return;
    const seatedPlayers = players.filter((p) => p.seatNumber > 0);
    const allReady = seatedPlayers.every((p) => p.isReady);
    if (seatedPlayers.length < config.maxPlayers || !allReady) {
      alert('需要所有玩家就位并准备');
      return;
    }
    // TODO: 开始游戏逻辑
    setRoom((prev) => (prev ? { ...prev, status: 'playing' } : null));
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: theme.colors['bg-primary'] }}
      >
        <span style={{ color: theme.colors['text-secondary'] }}>加载中...</span>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-20"
      style={{ backgroundColor: theme.colors['bg-primary'] }}
    >
      {/* 顶部导航栏 */}
      <header
        className="flex items-center justify-between px-4 py-3 border-b sticky top-0 z-50"
        style={{
          backgroundColor: theme.colors['bg-card'],
          borderColor: theme.colors['border-primary'],
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackClick}
            className="p-2 rounded-lg transition-opacity hover:opacity-80"
          >
            <ArrowLeft
              size={20}
              style={{ color: theme.colors['text-primary'] }}
            />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1
                className="font-bold text-lg"
                style={{ color: theme.colors['text-primary'] }}
              >
                {room?.roomCode}
              </h1>
              <span
                className="text-xs px-2 py-0.5 rounded"
                style={{
                  backgroundColor: `${theme.colors['accent-gold']}20`,
                  color: theme.colors['accent-gold'],
                }}
              >
                等待中
              </span>
            </div>
            <p
              className="text-xs"
              style={{ color: theme.colors['text-secondary'] }}
            >
              {players.filter((p) => p.seatNumber > 0).length}/{room?.maxPlayers} 人 · {room?.name}
            </p>
          </div>
        </div>

        {/* 房主信息 */}
        <div className="flex items-center gap-2">
          <Crown size={16} style={{ color: theme.colors['accent-gold'] }} />
          <span
            className="text-sm"
            style={{ color: theme.colors['text-primary'] }}
          >
            {players.find((p) => p.isHost)?.nickname}
          </span>
          <span
            className="text-xs px-1.5 py-0.5 rounded"
            style={{
              backgroundColor: theme.colors['accent-gold'],
              color: theme.colors['bg-primary'],
            }}
          >
            房主
          </span>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="p-4 space-y-4">
        {/* 房间配置 */}
        <RoomConfigPanel
          config={config}
          isHost={isHost}
          isLocked={room?.isLocked || false}
          onConfigChange={handleConfigChange}
          onLockConfig={handleLockConfig}
          onUnlockConfig={handleUnlockConfig}
        />

        {/* 玩家座位 */}
        <PlayerSeatGrid
          players={players}
          maxPlayers={room?.maxPlayers || 10}
          currentUserId={currentUserId}
          isHost={isHost}
          isSpectator={isSpectator}
          onSitDown={handleSitDown}
          onKickPlayer={handleKickPlayer}
          onTransferHost={handleTransferHost}
        />

        {/* 观战者列表 */}
        <SpectatorList spectators={spectators} isSpectator={isSpectator} />
      </main>

      {/* 底部操作栏 */}
      <footer
        className="fixed bottom-0 left-0 right-0 px-4 py-3"
        style={{
          backgroundColor: '#1A0F2A',
          borderTop: `1px solid ${theme.colors['border-primary']}`,
        }}
      >
        <div className="flex gap-3">
          {/* 退出房间按钮 */}
          <button
            onClick={handleLeaveRoom}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all active:scale-95"
            style={{
              backgroundColor: `${theme.colors['accent-purple']}30`,
              color: theme.colors['text-primary'],
              border: `1px solid ${theme.colors['accent-purple']}50`,
            }}
          >
            <LogOut size={18} />
            退出房间
          </button>

          {/* 开始游戏/准备按钮 */}
          {isHost ? (
            <button
              onClick={handleStartGame}
              disabled={
                players.filter((p) => p.seatNumber > 0).length < config.maxPlayers ||
                !players.filter((p) => p.seatNumber > 0).every((p) => p.isReady)
              }
              className="flex-[2] flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.colors['accent-gold'],
                color: theme.colors['bg-primary'],
              }}
            >
              <Play size={18} />
              开始游戏
            </button>
          ) : (
            <button
              onClick={() => {
                // 切换准备状态
                setPlayers((prev) =>
                  prev.map((p) =>
                    p.id === currentUserId ? { ...p, isReady: !p.isReady } : p
                  )
                );
              }}
              disabled={isSpectator}
              className="flex-[2] flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: players.find((p) => p.id === currentUserId)?.isReady
                  ? `${theme.colors['accent-purple']}50`
                  : theme.colors['accent-gold'],
                color: theme.colors['bg-primary'],
              }}
            >
              <Play size={18} />
              {players.find((p) => p.id === currentUserId)?.isReady
                ? '取消准备'
                : '准备'}
            </button>
          )}
        </div>
      </footer>

      {/* 密码输入弹窗 */}
      <PasswordInputModal
        isOpen={showPasswordModal}
        roomCode={room?.roomCode || ''}
        onClose={() => {
          setShowPasswordModal(false);
          setJoiningSeat(null);
        }}
        onConfirm={handlePasswordConfirm}
      />

      {/* 退出房间确认弹窗 */}
      <ConfirmModal
        isOpen={showLeaveConfirm}
        title="确认退出房间"
        message={isHost ? "你是房主，退出后将关闭房间，是否确认？" : "退出后需要重新加入房间，是否确认？"}
        confirmText="确认退出"
        cancelText="取消"
        onConfirm={confirmLeaveRoom}
        onCancel={() => setShowLeaveConfirm(false)}
      />

      {/* 返回确认弹窗 */}
      <ConfirmModal
        isOpen={showBackConfirm}
        title="确认返回大厅"
        message={isHost ? "你是房主，返回后将关闭房间，是否确认？" : "返回后需要重新加入房间，是否确认？"}
        confirmText="确认返回"
        cancelText="取消"
        onConfirm={confirmBack}
        onCancel={() => setShowBackConfirm(false)}
      />
    </div>
  );
};

export default OnlineGameRoom;
